import * as React from 'react';

import { WaitingOverlay } from './WaitingOverlay';

import { Bus, Events } from 'core/infrastructure';

import Keypad from './Keypad';
import { ScreenSize } from './ScreenSize';
import BaseComponent from './BaseComponent';
import { DialogActions, ScreenUtils } from './controls';
import { Flex } from './FlexContainer';

export interface ScreenBaseProps<TResult> {
	actions?: DialogActions<TResult>;
}

type ExtractGeneric<T> = T extends ScreenBaseProps<infer X> ? X : never;

export abstract class ScreenBase<P extends ScreenBaseProps<any>, S> extends BaseComponent<P, S> {

	// OJO: Esta clase *NO* debe tener estado propio porque si no se pueden producir problemas
	// cuando las clases hijas gestionar su parte de estado y machacan parte del estado de la
	// clase padre. Por eso el estado relacionado con la cortinilla de espera o la animación durante
	// conexiones al servidor está pasado al WaitingOverlay y al Header respectivamente


	private waitingOverlay = React.createRef<WaitingOverlay>();
	private screenRef = React.createRef<HTMLDivElement>();

	protected cancel = () => this.props.actions!.cancel();

	protected closeWith = (value: ExtractGeneric<P>) => this.props.actions!.closeWith(value);

	protected onBrowserBack = () => { };

	constructor(props: P) {
		super(props);
	}

	componentDidMount() {

		this.mounted = true;

		this.setupScreenClassAndStyle();

		this.on('browser:back', async ({ viewId }) => {
			if (viewId === this.getScreenNode().id)
				this.onBrowserBack();
		});

		this.onComponentDidMount();
	}


	private setupScreenClassAndStyle(): void {
		const screen = this.getScreenNode() as HTMLElement;
		const size = this.getScreenSize();
		if (size === 'small') {
			screen.classList.add('screen-small');
		}
		else if (this.getScreenSize() === 'medium') {
			screen.classList.add('screen-medium');
		}

		// Si es tamaño de la pantalla es personalizado y es un dispositivo grande,
		// tengo que ajustar el estilo. No puedo hacerlo sin el if del tema porque
		// no puedo usar mediaqueries para eso, como hago con las clases que ya
		// son ignoradas en las pantallas pequeñas
		if (this.isLargeDevice() && typeof size === 'object') {

			// Hay muchos casos en que sólo quiero cambiar la altura porque la anchura
			// quiero dejarla para que cuadre con el keypad. Permito dejar la anchura
			// indeterminada y usar la anchura por defecto.
			const width = size.width || 400;
			const height = size.height;

			// Uso variables css para que luego desde las devtools sea más sencillo
			// jugar con los valores de width y height y ajustarlos visualmente

			// Los max(...) para top/left son necesarios porque si la resolución
			// es menor que el tamaño del screen, por css está capado para que no
			// sobrepase el 100%, pero eso haría que el top/left quedasen negativos

			screen.style.setProperty('--height', `${height}px`);
			screen.style.setProperty('--width', `${width}px`);
			screen.style.top = 'max((100vh - var(--height))/2, 0px)';
			screen.style.height = 'var(--height)';
			screen.style.left = 'max((100vw - var(--width))/2, 0px)';
			screen.style.width = 'var(--width)';
		}
	}

	protected abstract getContent(): React.ReactNode;

	protected getScreenSize(): ScreenSize {
		return 'small';
	}

	protected getScreenNode(): HTMLElement {
		return ScreenUtils.getParentScreen(this.screenRef.current!);
	}

	protected isInForeground(): boolean {
		return ScreenUtils.isInTopScreen(this.screenRef.current!);
	}

	protected isInTopView(): boolean {
		return ScreenUtils.isInTopView(this.screenRef.current!);
	}

	protected useKeypad(): boolean {
		return true;
	}

	protected useKeyboard(): boolean {
		return true;
	}

	protected getOpacity(): number {
		return 1;
	}

	protected showMinimalHeader(): boolean {
		return false;
	}

	protected isLargeDevice(): boolean {
		return true;
	}

	protected getHeader(): React.ReactNode {
		return null;
	}

	protected getFooter(): React.ReactNode {
		return null;
	}

	protected enableContentScroll(): boolean {
		return true;
	}

	// el <T,> es porque si no el parser se enfada con el genérico en la función async arrow
	// OJO: A día de hoy esto no soporta reentradas porque se quitaría el overlay antes de
	// acabar el proceso completo. Podría hacerlo tolerante, pero no tiene sentido. Deberíamos
	// controlar bien el proceso completo desde fuera
	public execute = async <T,>({ action, overlay, waitForPendingRequests }: {
		waitForPendingRequests?: boolean,
		overlay?: 'on-start' | 'on-first-request'
		action: () => Promise<T>
	}): Promise<T> => {

		overlay = overlay || 'on-start';

		let unregisterEventHandler: Nullable<() => void> = null;

		if (overlay === 'on-first-request') {
			unregisterEventHandler = Events.on('request:started', async () => {
				await this.waitingOverlay?.current?.show();
			});
		}

		try {
			const willWaitForPendingRequests = waitForPendingRequests && Bus.getPendingRequests().length;

			if (overlay === 'on-start' || willWaitForPendingRequests) {
				await this.waitingOverlay?.current?.show();
			}

			if (waitForPendingRequests)
				await Bus.waitForPendingRequests();

			return await action();

		} finally {
			unregisterEventHandler?.();
			this.waitingOverlay?.current?.hide();
		}
	};

	render() {

		const Header = this.getHeader();
		const Footer = this.getFooter();
		const Content = this.getContent();

		return <div className='screen'>
			<Flex.Vertical ref={this.screenRef} style={{ opacity: this.getOpacity() }}>
				{Header ? <Flex.Item>{Header}</Flex.Item> : null}
				<Flex.Item size='*' style={this.enableContentScroll() ? undefined : { overflow: 'none', height: '100%' }}>
					{Content}
				</Flex.Item>

				{Footer ? <Flex.Item>{Footer}</Flex.Item> : null}
				<Flex.Item>
					{
						this.useKeypad()
							? <Keypad screenSize={this.getScreenSize()} isLargeDevice={this.isLargeDevice()} />
							: null
					}
				</Flex.Item>
			</Flex.Vertical>
			<WaitingOverlay ref={this.waitingOverlay} minimalHeader={this.showMinimalHeader()} />
		</div>;
	}
}


export abstract class StatelessScreen<P extends ScreenBaseProps<any>> extends ScreenBase<P, EmptyState> {
	createInitialState(_props: P) {
		return null;
	}
}